<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title>
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          {{ $t('golf_courts') }}
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="dialog = true">
            + {{ $t('add_golf_court') }}
          </v-btn>
        </v-card-title>

        <v-data-table :headers="headers" :items="courts" :loading="loading" class="elevation-1"
          :no-data-text="$t('no_records_yet')">
          <template #item.status="{ item }">
            <v-chip :color="getStatusColor(item.status)" dark small>
              {{ getStatusText(item.status) }}
            </v-chip>
          </template>
          <template #item.actions="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon small class="mr-2" color="primary" v-bind="attrs" v-on="on" @click="openTees(item)">
                  mdi-golf-tee
                </v-icon>
              </template>
              <span>Configurar salidas</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon small class="mr-2" v-bind="attrs" v-on="on" @click="viewDetails(item)">
                  mdi-eye
                </v-icon>
              </template>
              <span>{{ $t('view_details') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon small class="mr-2" v-bind="attrs" v-on="on" @click="editItem(item)">
                  mdi-pencil
                </v-icon>
              </template>
              <span>{{ $t('edit') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon small v-bind="attrs" v-on="on" @click="confirmDelete(item)">
                  mdi-delete
                </v-icon>
              </template>
              <span>{{ $t('delete_action') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <!-- Dialog para crear o editar un campo -->
    <v-dialog v-model="dialog" max-width="700px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-container>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="editedItem.name" :label="$t('golf_court_name')"
                    :rules="[rules.required]"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="editedItem.course_type" :items="courseTypes" item-text="text" item-value="value"
                    :label="$t('golf_court_type')" :rules="[rules.required]"></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="editedItem.total_holes" type="number" :label="$t('total_holes')"
                    :rules="[rules.required, rules.positiveNumber]"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="editedItem.hole_range_start" type="number" :label="$t('hole_range_start')"
                    :rules="[rules.required, rules.positiveNumber]"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="editedItem.hole_range_end" type="number" :label="$t('hole_range_end')"
                    :rules="[rules.required, rules.positiveNumber]"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="editedItem.minutes_per_hole" type="number" :label="$t('minutes_per_hole')"
                    :rules="[rules.required, rules.positiveNumber]"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu ref="menuStart" v-model="menuStart" :close-on-content-click="false" :nudge-right="40"
                    :return-value.sync="editedItem.opening_time" transition="scale-transition" offset-y
                    max-width="290px" min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="editedItem.opening_time" :label="$t('opening_time')"
                        prepend-icon="mdi-clock-time-four-outline" readonly v-bind="attrs" v-on="on"
                        :rules="[rules.required]"></v-text-field>
                    </template>
                    <v-time-picker v-if="menuStart" v-model="editedItem.opening_time" full-width
                      @click:minute="$refs.menuStart.save(editedItem.opening_time)"></v-time-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu ref="menuEnd" v-model="menuEnd" :close-on-content-click="false" :nudge-right="40"
                    :return-value.sync="editedItem.closing_time" transition="scale-transition" offset-y
                    max-width="290px" min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="editedItem.closing_time" :label="$t('closing_time')"
                        prepend-icon="mdi-clock-time-four-outline" readonly v-bind="attrs" v-on="on"
                        :rules="[rules.required]"></v-text-field>
                    </template>
                    <v-time-picker v-if="menuEnd" v-model="editedItem.closing_time" full-width
                      @click:minute="$refs.menuEnd.save(editedItem.closing_time)"></v-time-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12">
                  <p class="caption grey--text mb-2">{{ $t('opening_days') }}</p>
                  <v-btn-toggle v-model="selectedDays" multiple mandatory>
                    <v-btn v-for="(day, index) in daysOfWeek" :key="index" :value="index.toString()" text>
                      {{ $t(day) }}
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
                <v-col cols="12">
                  <v-select v-model="editedItem.status" :items="courtStatuses" item-text="text" item-value="value"
                    :label="$t('estado')" :rules="[rules.required]" outlined dense></v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="close">{{ $t('cancelar') }}</v-btn>
          <v-btn color="blue darken-1" text @click="save" :disabled="!valid">{{ $t('guardar') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Configuración de tees / salidas -->
    <v-dialog v-model="teesDialog" max-width="1050px" persistent scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          <div>
            <div>Salidas de {{ selectedCourse ? selectedCourse.name : '' }}</div>
            <div class="text-caption">Rating, slope y distancia oficial por tee</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeTees"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="pa-5">
          <v-alert v-if="teeError" type="error" dense dismissible @input="teeError = null">
            {{ teeError }}
          </v-alert>

          <v-alert type="info" outlined dense>
            El rating mide la dificultad para un jugador scratch y el slope la dificultad relativa
            (valor permitido: 55 a 155).
            <span v-if="selectedCourseIs18">
              Para 18 hoyos también se requieren los valores oficiales de cada vuelta de 9 hoyos.
            </span>
          </v-alert>

          <div class="d-flex align-center mb-3">
            <h3>Salidas configuradas</h3>
            <v-spacer></v-spacer>
            <v-btn color="primary" small @click="newTee">
              <v-icon left small>mdi-plus</v-icon>Nueva salida
            </v-btn>
          </div>

          <v-data-table
            :headers="teeHeaders"
            :items="tees"
            :loading="loadingTees"
            no-data-text="Esta cancha todavía no tiene salidas configuradas"
            hide-default-footer
            class="mb-5"
          >
            <template v-slot:item.color="{ item }">
              <v-chip small outlined>{{ item.color_display || teeColorLabel(item.color) }}</v-chip>
            </template>
            <template v-slot:item.rating="{ item }">{{ item.rating }} / {{ item.slope }}</template>
            <template v-slot:item.first_nine="{ item }">
              {{ formatRatingSlope(item.first_nine_rating, item.first_nine_slope) }}
            </template>
            <template v-slot:item.second_nine="{ item }">
              {{ formatRatingSlope(item.second_nine_rating, item.second_nine_slope) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small color="primary" @click="editTee(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
              <v-btn icon small color="error" @click="deleteTee(item)"><v-icon small>mdi-delete</v-icon></v-btn>
            </template>
          </v-data-table>

          <v-expand-transition>
            <v-card v-if="teeFormVisible" outlined>
              <v-card-title class="subtitle-1">
                {{ teeEditingId ? 'Editar salida' : 'Nueva salida' }}
              </v-card-title>
              <v-card-text>
                <v-form ref="teeForm" v-model="teeFormValid" lazy-validation>
                  <v-row dense>
                    <v-col cols="12" sm="4">
                      <v-text-field v-model.trim="teeForm.name" label="Nombre" placeholder="Ej. Blancas"
                        outlined dense :rules="[rules.required]" />
                    </v-col>
                    <v-col cols="6" sm="4">
                      <v-select v-model="teeForm.color" :items="teeColors" item-text="text" item-value="value"
                        label="Color" outlined dense :rules="[rules.required]" />
                    </v-col>
                    <v-col cols="6" sm="4">
                      <v-select v-model="teeForm.gender" :items="teeGenders" item-text="text" item-value="value"
                        label="Categoría" outlined dense :rules="[rules.required]" />
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field v-model.number="teeForm.rating" type="number" step="0.1" label="Course Rating"
                        outlined dense :rules="[rules.required, rules.positiveNumber]" />
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field v-model.number="teeForm.slope" type="number" min="55" max="155" label="Slope Rating"
                        outlined dense :rules="[rules.required, rules.slope]" />
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field v-model.number="teeForm.total_distance" type="number" min="1" label="Distancia total (m)"
                        outlined dense clearable :rules="[rules.optionalPositiveNumber]" />
                    </v-col>

                    <template v-if="selectedCourseIs18">
                      <v-col cols="12"><v-divider class="mb-3" /><strong>Primera vuelta · hoyos 1–9</strong></v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model.number="teeForm.first_nine_rating" type="number" step="0.1"
                          label="Rating primera vuelta" outlined dense :rules="[rules.required, rules.positiveNumber]" />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model.number="teeForm.first_nine_slope" type="number" min="55" max="155"
                          label="Slope primera vuelta" outlined dense :rules="[rules.required, rules.slope]" />
                      </v-col>
                      <v-col cols="12"><strong>Segunda vuelta · hoyos 10–18</strong></v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model.number="teeForm.second_nine_rating" type="number" step="0.1"
                          label="Rating segunda vuelta" outlined dense :rules="[rules.required, rules.positiveNumber]" />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model.number="teeForm.second_nine_slope" type="number" min="55" max="155"
                          label="Slope segunda vuelta" outlined dense :rules="[rules.required, rules.slope]" />
                      </v-col>
                    </template>
                  </v-row>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="cancelTeeForm">Cancelar</v-btn>
                <v-btn color="primary" :loading="savingTee" :disabled="!teeFormValid" @click="saveTee">
                  Guardar salida
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-expand-transition>

          <v-divider class="my-6"></v-divider>
          <div class="d-flex align-center mb-2">
            <div>
              <h3>Configuración de hoyos</h3>
              <div class="text-caption grey--text">
                El índice 1 corresponde al hoyo más difícil. Los índices no pueden repetirse.
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn color="primary" outlined small :loading="savingHoles" @click="saveHoles">
              <v-icon left small>mdi-content-save</v-icon>Guardar hoyos
            </v-btn>
          </div>

          <v-simple-table dense class="hole-table">
            <thead>
              <tr>
                <th>Hoyo</th>
                <th>Par</th>
                <th>Índice caballeros</th>
                <th>Índice damas (opcional)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loadingHoles">
                <td colspan="4" class="text-center py-5"><v-progress-circular indeterminate color="primary" /></td>
              </tr>
              <tr v-for="hole in holes" v-else :key="hole.hole_number">
                <td class="font-weight-bold">{{ hole.hole_number }}</td>
                <td>
                  <v-select v-model.number="hole.par" :items="[3, 4, 5]" dense outlined hide-details />
                </td>
                <td>
                  <v-text-field v-model.number="hole.handicap_index" type="number" min="1" max="18"
                    dense outlined hide-details />
                </td>
                <td>
                  <v-text-field v-model.number="hole.handicap_index_ladies" type="number" min="1" max="18"
                    dense outlined hide-details clearable />
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog for Details -->
    <v-dialog v-model="dialogDetails" max-width="600px">
      <v-card>
        <v-card-title class="headline grey lighten-2">
          {{ $t('golf_court_details') }}
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogDetails = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-list dense>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('nombre') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ viewItem.name }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('golf_court_type') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ getCourseTypeName(viewItem.course_type) }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('total_holes') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ viewItem.total_holes }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('hole_range') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ viewItem.hole_range_start }} - {{ viewItem.hole_range_end
                }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('schedule') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ viewItem.opening_time }} - {{ viewItem.closing_time
                }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('minutes_per_hole') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ viewItem.minutes_per_hole }} min</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('days_open') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ formatDays(viewItem.opening_days) }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('estado') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">
                <v-chip :color="getStatusColor(viewItem.status)" dark small>
                  {{ getStatusText(viewItem.status) }}
                </v-chip>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="dialogDelete" max-width="400px">
      <v-card>
        <v-card-title class="headline">{{ $t('delete_golf_court_title') }}</v-card-title>
        <v-card-text>{{ $t('delete_golf_court_confirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeDelete">{{ $t('cancelar') }}</v-btn>
          <v-btn color="red darken-1" text @click="deleteItemConfirm">{{ $t('delete_action') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'

const API_BASE_URL = 'https://clubtest.intrare.app'
const IdSport = '91b08c78-baf5-49ab-b488-36af5867fb3b'
const IdHousingUnit = 'a58d8679-e2ae-4055-a243-c63d438f19eb'

function emptyTeeForm () {
  return {
    name: '',
    color: 'white',
    gender: 'any',
    rating: null,
    slope: null,
    first_nine_rating: null,
    first_nine_slope: null,
    second_nine_rating: null,
    second_nine_slope: null,
    total_distance: null
  }
}

export default {
  name: 'GolfCourts',
  data: () => ({
    dialog: false,
    dialogDetails: false,
    dialogDelete: false,
    loading: false,
    valid: true,
    menuStart: false,
    menuEnd: false,
    teesDialog: false,
    teeFormVisible: false,
    teeFormValid: true,
    teeEditingId: null,
    selectedCourse: null,
    tees: [],
    holes: [],
    teeForm: emptyTeeForm(),
    loadingTees: false,
    loadingHoles: false,
    savingTee: false,
    savingHoles: false,
    teeError: null,
    itemToDelete: null,
    courts: [],
    editedIndex: -1,
    editedItem: {
      idSport: IdSport,
      IdHousingUnit: IdHousingUnit,
      name: '',
      course_type: 2,
      total_holes: 18,
      hole_range_start: 1,
      hole_range_end: 18,
      minutes_per_hole: 10,
      time_interval: 10,
      opening_days: '0,1,2,3,4,5,6',
      opening_time: '06:00:00',
      closing_time: '22:00:00',
      status: 1
    },
    defaultItem: {
      idSport: IdSport,
      IdHousingUnit: IdHousingUnit,
      name: '',
      course_type: 2,
      total_holes: 18,
      hole_range_start: 1,
      hole_range_end: 18,
      minutes_per_hole: 10,
      time_interval: 10,
      opening_days: '0,1,2,3,4,5,6',
      opening_time: '06:00:00',
      closing_time: '22:00:00',
      status: 1
    },
    viewItem: {},
    daysOfWeek: ['dom', 'lun', 'mar', 'mie', 'jue', 'vie', 'sab'],
    selectedDays: []
  }),

  computed: {
    formTitle () {
      return this.editedIndex === -1 ? this.$t('new_golf_court') : this.$t('edit_golf_court')
    },
    headers () {
      return [
        { text: this.$t('nombre'), value: 'name' },
        { text: this.$t('total_holes'), value: 'total_holes' },
        { text: this.$t('opening_time'), value: 'opening_time' },
        { text: this.$t('closing_time'), value: 'closing_time' },
        { text: this.$t('estado'), value: 'status' },
        { text: this.$t('acciones'), value: 'actions', sortable: false }
      ]
    },
    courseTypes () {
      return [
        { text: this.$t('standard'), value: 1 },
        { text: this.$t('professional'), value: 2 },
        { text: this.$t('practice'), value: 3 }
      ]
    },
    rules () {
      return {
        required: value => !!value || this.$t('required_field'),
        positiveNumber: value => value > 0 || this.$t('positive_number_error'),
        optionalPositiveNumber: value => value === null || value === '' || value > 0 || this.$t('positive_number_error'),
        slope: value => (Number(value) >= 55 && Number(value) <= 155) || 'El slope debe estar entre 55 y 155'
      }
    },
    selectedCourseIs18 () {
      return Number(this.selectedCourse?.total_holes) === 18
    },
    teeHeaders () {
      const headers = [
        { text: 'Salida', value: 'name' },
        { text: 'Color', value: 'color' },
        { text: 'Categoría', value: 'gender_display' },
        { text: 'Rating / Slope', value: 'rating' },
        { text: 'Distancia (m)', value: 'total_distance' }
      ]
      if (this.selectedCourseIs18) {
        headers.push(
          { text: '1–9', value: 'first_nine' },
          { text: '10–18', value: 'second_nine' }
        )
      }
      headers.push({ text: '', value: 'actions', sortable: false })
      return headers
    },
    teeColors () {
      return [
        { text: 'Azul', value: 'blue' },
        { text: 'Blanco', value: 'white' },
        { text: 'Dorado', value: 'gold' },
        { text: 'Rojo', value: 'red' }
      ]
    },
    teeGenders () {
      return [
        { text: 'Caballeros', value: 'male' },
        { text: 'Damas', value: 'female' },
        { text: 'Ambos', value: 'any' }
      ]
    },
    courtStatuses () {
      return [
        { text: this.$t('court_status_open'), value: 1 },
        { text: this.$t('court_status_rain'), value: 2 },
        { text: this.$t('court_status_storm'), value: 3 },
        { text: this.$t('court_status_minor_damage'), value: 4 },
        { text: this.$t('court_status_damaged_greens'), value: 5 },
        { text: this.$t('court_status_maintenance'), value: 6 }
      ]
    }
  },

  created () {
    this.initializeComponent()
  },

  watch: {
    dialog (val) {
      val || this.close()
    },
    dialogDelete (val) {
      val || this.closeDelete()
    },
    selectedDays (val) {
      if (val) {
        this.editedItem.opening_days = val.join(',')
      }
    }
  },

  methods: {

    async initializeComponent () {
      await this.getCourts()
    },

    async getCourts () {
      this.loading = true
      try {
        const idHousingUnit = store.getters.getProfile.idHousingUnit || IdHousingUnit
        const response = await this.apiCall('get', `/golf-courses/?housing_unit=${idHousingUnit}`)
        console.log(response.data)
        this.courts = response.data
      } catch (error) {
        console.error('Error fetching courts:', error)
      } finally {
        this.loading = false
      }
    },

    apiCall (method, endpoint, data = null) {
      const config = {
        method,
        url: `${API_BASE_URL}${endpoint}`,
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        }
      }

      if (data) {
        config.data = data
      }

      return axios(config)
    },

    async openTees (course) {
      this.selectedCourse = course
      this.teesDialog = true
      this.teeFormVisible = false
      this.teeError = null
      await Promise.all([this.loadTees(), this.loadHoles()])
    },

    async loadTees () {
      if (!this.selectedCourse) return
      this.loadingTees = true
      try {
        const response = await this.apiCall('get', `/golf-tees/?course=${this.selectedCourse.id}`)
        this.tees = response.data.results || response.data || []
      } catch (error) {
        this.teeError = this.apiErrorMessage(error)
      } finally {
        this.loadingTees = false
      }
    },

    async loadHoles () {
      if (!this.selectedCourse) return
      this.loadingHoles = true
      try {
        const response = await this.apiCall('get', `/golf-holes/?course=${this.selectedCourse.id}`)
        const configured = response.data.results || response.data || []
        const byNumber = configured.reduce((result, hole) => {
          result[hole.hole_number] = hole
          return result
        }, {})
        const start = Number(this.selectedCourse.hole_range_start)
        const end = Number(this.selectedCourse.hole_range_end)
        this.holes = []
        for (let number = start; number <= end; number += 1) {
          const current = byNumber[number]
          this.holes.push(current ? {
            ...current,
            par: Number(current.par),
            handicap_index: Number(current.handicap_index),
            handicap_index_ladies: current.handicap_index_ladies === null ? null : Number(current.handicap_index_ladies)
          } : {
            id: null,
            hole_number: number,
            par: 4,
            handicap_index: number - start + 1,
            handicap_index_ladies: null
          })
        }
      } catch (error) {
        this.teeError = this.apiErrorMessage(error)
      } finally {
        this.loadingHoles = false
      }
    },

    async saveHoles () {
      const validationError = this.validateHoleRows()
      if (validationError) {
        this.teeError = validationError
        return
      }
      this.savingHoles = true
      this.teeError = null
      try {
        await Promise.all(this.holes.map(hole => {
          const payload = {
            course: this.selectedCourse.id,
            hole_number: Number(hole.hole_number),
            par: Number(hole.par),
            handicap_index: Number(hole.handicap_index),
            handicap_index_ladies: hole.handicap_index_ladies === null || hole.handicap_index_ladies === ''
              ? null
              : Number(hole.handicap_index_ladies)
          }
          return hole.id
            ? this.apiCall('put', `/golf-holes/${hole.id}/`, payload)
            : this.apiCall('post', '/golf-holes/', payload)
        }))
        await this.loadHoles()
      } catch (error) {
        this.teeError = this.apiErrorMessage(error)
      } finally {
        this.savingHoles = false
      }
    },

    validateHoleRows () {
      if (!this.holes.length) return 'La cancha no tiene un rango válido de hoyos.'
      if (this.holes.some(hole => ![3, 4, 5].includes(Number(hole.par)))) return 'El par de cada hoyo debe ser 3, 4 o 5.'
      const men = this.holes.map(hole => Number(hole.handicap_index))
      if (men.some(value => value < 1 || value > 18) || new Set(men).size !== men.length) {
        return 'Los índices de caballeros deben estar entre 1 y 18 y no pueden repetirse.'
      }
      const ladies = this.holes.map(hole => hole.handicap_index_ladies).filter(value => value !== null && value !== '')
      if (ladies.length && ladies.length !== this.holes.length) return 'Capture el índice de damas en todos los hoyos o déjelos todos vacíos.'
      const ladiesNumbers = ladies.map(Number)
      if (ladiesNumbers.some(value => value < 1 || value > 18) || new Set(ladiesNumbers).size !== ladiesNumbers.length) {
        return 'Los índices de damas deben estar entre 1 y 18 y no pueden repetirse.'
      }
      return null
    },

    newTee () {
      this.teeEditingId = null
      this.teeForm = emptyTeeForm()
      this.teeFormVisible = true
      this.teeError = null
      this.$nextTick(() => this.$refs.teeForm && this.$refs.teeForm.resetValidation())
    },

    editTee (tee) {
      this.teeEditingId = tee.id
      this.teeForm = {
        name: tee.name,
        color: tee.color,
        gender: tee.gender,
        rating: tee.rating === null ? null : Number(tee.rating),
        slope: tee.slope === null ? null : Number(tee.slope),
        first_nine_rating: tee.first_nine_rating === null ? null : Number(tee.first_nine_rating),
        first_nine_slope: tee.first_nine_slope === null ? null : Number(tee.first_nine_slope),
        second_nine_rating: tee.second_nine_rating === null ? null : Number(tee.second_nine_rating),
        second_nine_slope: tee.second_nine_slope === null ? null : Number(tee.second_nine_slope),
        total_distance: tee.total_distance === null ? null : Number(tee.total_distance)
      }
      this.teeFormVisible = true
      this.teeError = null
      this.$nextTick(() => this.$refs.teeForm && this.$refs.teeForm.resetValidation())
    },

    cancelTeeForm () {
      this.teeFormVisible = false
      this.teeEditingId = null
      this.teeForm = emptyTeeForm()
    },

    async saveTee () {
      if (!this.$refs.teeForm.validate()) return
      this.savingTee = true
      this.teeError = null
      const payload = {
        ...this.teeForm,
        course: this.selectedCourse.id
      }
      if (!this.selectedCourseIs18) {
        payload.first_nine_rating = null
        payload.first_nine_slope = null
        payload.second_nine_rating = null
        payload.second_nine_slope = null
      }
      try {
        if (this.teeEditingId) {
          await this.apiCall('put', `/golf-tees/${this.teeEditingId}/`, payload)
        } else {
          await this.apiCall('post', '/golf-tees/', payload)
        }
        await this.loadTees()
        this.cancelTeeForm()
      } catch (error) {
        this.teeError = this.apiErrorMessage(error)
      } finally {
        this.savingTee = false
      }
    },

    async deleteTee (tee) {
      if (!window.confirm(`¿Eliminar la salida ${tee.name}?`)) return
      this.loadingTees = true
      this.teeError = null
      try {
        await this.apiCall('delete', `/golf-tees/${tee.id}/`)
        await this.loadTees()
        if (this.teeEditingId === tee.id) this.cancelTeeForm()
      } catch (error) {
        this.teeError = this.apiErrorMessage(error)
      } finally {
        this.loadingTees = false
      }
    },

    closeTees () {
      this.teesDialog = false
      this.selectedCourse = null
      this.tees = []
      this.holes = []
      this.teeError = null
      this.cancelTeeForm()
    },

    teeColorLabel (color) {
      return this.teeColors.find(item => item.value === color)?.text || color
    },

    formatRatingSlope (rating, slope) {
      if (rating === null || rating === undefined || slope === null || slope === undefined) return 'Pendiente'
      return `${rating} / ${slope}`
    },

    apiErrorMessage (error) {
      const data = error.response?.data
      if (typeof data === 'string') return data
      if (data?.detail) return data.detail
      if (data && typeof data === 'object') return Object.values(data).flat().join(' ')
      return 'No fue posible guardar la configuración de la salida.'
    },

    editItem (item) {
      this.editedIndex = this.courts.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.selectedDays = item.opening_days ? item.opening_days.split(',') : []
      this.dialog = true
    },

    viewDetails (item) {
      this.viewItem = Object.assign({}, item)
      this.dialogDetails = true
    },

    confirmDelete (item) {
      this.itemToDelete = item
      this.dialogDelete = true
    },

    async deleteItemConfirm () {
      if (this.itemToDelete) {
        this.loading = true
        try {
          await this.apiCall('delete', `/golf-courses/${this.itemToDelete.id}/`)
          const index = this.courts.indexOf(this.itemToDelete)
          this.courts.splice(index, 1)
        } catch (error) {
          console.error('Error deleting court:', error)
        } finally {
          this.loading = false
          this.closeDelete()
        }
      }
    },

    closeDelete () {
      this.dialogDelete = false
      this.$nextTick(() => {
        this.itemToDelete = null
      })
    },

    close () {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
        this.selectedDays = this.defaultItem.opening_days.split(',')
        this.$refs.form.resetValidation()
      })
    },

    async save () {
      if (!this.$refs.form.validate()) return

      this.loading = true
      try {
        if (this.editedIndex > -1) {
          // Editar
          const response = await this.apiCall('put', `/golf-courses/${this.editedItem.id}/`, this.editedItem)
          Object.assign(this.courts[this.editedIndex], response.data)
        } else {
          // Crear
          const response = await this.apiCall('post', '/golf-courses/', this.editedItem)
          this.courts.push(response.data)
        }
        this.close()
      } catch (error) {
        console.error('Error saving court:', error)
      } finally {
        this.loading = false
      }
    },

    getCourseTypeName (value) {
      const type = this.courseTypes.find(t => t.value === value)
      return type ? type.text : this.$t('unknown')
    },

    formatDays (daysString) {
      if (!daysString) return ''
      const days = daysString.split(',')
      return days.map(d => this.$t(this.daysOfWeek[parseInt(d)])).join(', ')
    },

    getStatusColor (status) {
      const colors = {
        1: 'green',
        2: 'blue darken-2',
        3: 'purple darken-2',
        4: 'orange darken-2',
        5: 'brown',
        6: 'grey darken-3'
      }
      return colors[status] || 'grey'
    },

    getStatusText (status) {
      const statusObj = this.courtStatuses.find(s => s.value === status)
      return statusObj ? statusObj.text : this.$t('unknown')
    }
  }
}
</script>
